// Bölüm 1 "Talep Bilgisi Hatalı": İSG'nin önerdiği değerler ve firma tarafı kontrolü
import { useRef } from 'react';
import { toast } from 'sonner';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { WORK_PERMIT_TYPES } from '../constants';

export const INFO_FIELDS = {
  location: { label: 'Lokasyon', fields: ['location_id'] },
  on_behalf_of: { label: 'Başka Firma Adına', fields: ['on_behalf_of'] },
  job_type: { label: 'İş Tipi / İş Çeşidi', fields: ['is_tipi', 'is_cesidi'] },
  start: { label: 'Başlangıç Tarihi ve Saati', fields: ['start_date', 'start_time'] },
  end: { label: 'Bitiş Tarihi ve Saati', fields: ['end_date', 'end_time'] },
  job_description: { label: 'İş Tanımı', fields: ['job_description'] },
  work_permit_types: { label: 'İş İzni Türleri', fields: ['work_permit_types'] },
};

const norm = (v) => (Array.isArray(v) ? [...v].sort().join('|') : String(v ?? '').split(/\s+/).filter(Boolean).join(' '));

export const pickFields = (key, data) => Object.fromEntries(INFO_FIELDS[key].fields.map(f => [f, data?.[f] ?? (f === 'work_permit_types' ? [] : '')]));

export const correctionMatches = (key, formData, expected) => INFO_FIELDS[key].fields.every(f => norm(formData?.[f]) === norm(expected?.[f]));

export const unmatchedKeys = (corrections, formData) => Object.keys(corrections || {}).filter(k => !correctionMatches(k, formData, corrections[k].expected));

const fmtDate = (d) => (d ? d.split('-').reverse().join('.') : '-');

export const displayValue = (key, v = {}, locations = []) => {
  switch (key) {
    case 'location': return locations.find(l => l.id === v.location_id)?.name || '-';
    case 'job_type': return [v.is_tipi, v.is_cesidi].filter(Boolean).join(' / ') || '-';
    case 'start': return `${fmtDate(v.start_date)} ${v.start_time || ''}`.trim();
    case 'end': return `${fmtDate(v.end_date)} ${v.end_time || ''}`.trim();
    case 'work_permit_types': return (v.work_permit_types || []).map(id => WORK_PERMIT_TYPES.find(t => t.id === id)?.label || id).join(', ') || 'Yok';
    case 'on_behalf_of': return v.on_behalf_of?.trim() || 'Kendi adına (boş)';
    default: return v.job_description || '-';
  }
};

// Firma: girilen değerin altında İSG önerisi (eşleşince yeşil "OK", değilse kırmızı)
export const CorrectionHint = ({ fieldKey, correction, formData, locations }) => {
  if (!correction) return null;
  const ok = correctionMatches(fieldKey, formData, correction.expected);
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${ok ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-rose-300 bg-rose-50 text-rose-700'}`} data-testid={`correction-hint-${fieldKey}`}>
      {ok ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
      <div className="flex-1 min-w-0">
        <span className="font-semibold">Önerilen: </span>
        <span className="break-words whitespace-pre-wrap" data-testid={`correction-expected-${fieldKey}`}>{displayValue(fieldKey, correction.expected, locations)}</span>
        {!ok && <span className="block text-rose-500 mt-0.5">Girdiğiniz: {displayValue(fieldKey, correction.entered, locations)} – lütfen önerilen bilgiyi girin</span>}
      </div>
      {ok && <span className="font-bold" data-testid={`correction-ok-${fieldKey}`}>OK</span>}
    </div>
  );
};

// Firma: önerilen bilgi girilmeden alanın dışına çıkılırsa uyarır
export const CorrectionGuard = ({ fieldKey, correction, formData, children }) => {
  const ref = useRef(null);
  const okRef = useRef(true);
  okRef.current = !correction || correctionMatches(fieldKey, formData, correction.expected);
  if (!correction) return children;
  const onBlur = () => setTimeout(() => {
    const active = document.activeElement;
    if (ref.current?.contains(active) || active?.closest?.('[data-radix-popper-content-wrapper]')) return;
    if (!okRef.current) toast.warning('Lütfen önce önerilen bilgiyi girin', { id: `correction-${fieldKey}`, description: INFO_FIELDS[fieldKey].label });
  }, 350);
  return <div ref={ref} onBlur={onBlur} className="space-y-2">{children}</div>;
};
